import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, DollarSign, Users, MessageCircle, BarChart3, LogOut, Eye, CheckCircle, Clock, Image, Plus, Trash2, Upload, Video, Handshake, Link as LinkIcon, ExternalLink, QrCode, Ticket, Sparkles, RotateCw, ChevronDown, ChevronUp, X, ZoomIn, Inbox, FileText, Camera, Instagram, TrendingUp, Gift, Copy, Check, Car, Receipt, Bug, Wallet, Flame, LifeBuoy, Shield, Wand2, SlidersHorizontal, Lightbulb, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import PipelineDashboardInline from "@/pages/PipelineDashboard";
import InstagramManagerInline from "@/pages/InstagramManager";
import ApprovalsBadge from "@/components/ApprovalsBadge";
import AdminQrTab from "@/components/AdminQrTab";
import AdminWatermarkTab from "@/components/AdminWatermarkTab";
import AdminStudioTab from "@/components/AdminStudioTab";
import AdminReferralsTab from "@/pages/AdminReferralsTab";
import AdminChauffeurTab from "@/pages/AdminChauffeurTab";
import AdminManualPaymentsTab from "@/pages/AdminManualPaymentsTab";
import AdminClientErrorsTab from "@/pages/AdminClientErrorsTab";
import AdminHeatmapTab from "@/pages/AdminHeatmapTab";
import AdminSupportTab from "@/pages/AdminSupportTab";
import AdminPayoutsTab from "@/pages/AdminPayoutsTab";
import TopAiTools from "@/components/TopAiTools";
import TopCtaClicks from "@/components/TopCtaClicks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [sponsoredVideos, setSponsoredVideos] = useState([]);
  const [affiliatesData, setAffiliatesData] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [adminEvents, setAdminEvents] = useState([]);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", description: "", event_type: "General", event_date: "", location: "", cover_image: "", tags: "" });
  const [eventMediaForm, setEventMediaForm] = useState({ event_id: "", url: "", filename: "", media_type: "image" });
  const [eventUploadId, setEventUploadId] = useState("");
  const [eventUploading, setEventUploading] = useState(false);
  const [checkinList, setCheckinList] = useState([]);
  const [checkinEventId, setCheckinEventId] = useState(null);
  const [ticketList, setTicketList] = useState([]);
  const [ticketEventId, setTicketEventId] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [affiliateForm, setAffiliateForm] = useState({ company_name: "", contact_email: "", slug: "", commission_type: "percentage", commission_rate: 10, description: "" });
  const [affiliateLinkForm, setAffiliateLinkForm] = useState({ affiliate_id: "", title: "", description: "", url: "", image_url: "", product_type: "service", price: "" });
  const [showCreateAffiliate, setShowCreateAffiliate] = useState(false);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ client_email: "", title: "", event_type: "", event_date: "", description: "" });
  const [addMediaForm, setAddMediaForm] = useState({ gallery_id: "", url: "", filename: "", media_type: "image" });
  const [uploadGalleryId, setUploadGalleryId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [expandedGallery, setExpandedGallery] = useState(null);
  const [galleryMedia, setGalleryMedia] = useState([]);
  const [expandedEventMedia, setExpandedEventMedia] = useState(null);
  const [eventMediaList, setEventMediaList] = useState([]);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [ugcItems, setUgcItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [behaviorData, setBehaviorData] = useState(null);
  const [aiRecs, setAiRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [suggestionsTabData, setSuggestionsTabData] = useState({ items: [], counts: {} });
  const [suggestionsDigest, setSuggestionsDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Ensure token is in localStorage (from login state or existing)
    const stateToken = location.state?.token;
    if (stateToken) {
      localStorage.setItem("auth_token", stateToken);
    }

    const fetchAll = async () => {
      // Promise.allSettled — a single endpoint 500 (e.g. /admin/ai-insights
      // when the suggestion schema drifts) MUST NOT kill the entire dashboard
      // and force-logout the admin. Each result is unwrapped individually
      // so partial failures degrade gracefully (the affected tab just shows
      // empty rather than the whole page bouncing to /admin).
      const endpoints = [
        ["/admin/analytics", setAnalytics],
        ["/bookings", setBookings],
        ["/contacts", setContacts],
        ["/admin/payments", setPayments],
        ["/admin/clients", setClients],
        ["/admin/galleries", setGalleries],
        ["/admin/sponsored-videos", setSponsoredVideos],
        ["/admin/affiliates", setAffiliatesData],
        ["/admin/reviews", setAllReviews],
        ["/admin/ai-insights", setAiInsights],
        ["/admin/suggestions", (data) => setAllSuggestions(data?.items || [])],
        ["/admin/events", setAdminEvents],
      ];

      try {
        // Authoritative auth check — only THIS endpoint failing bounces.
        await api.get("/auth/me");
      } catch {
        toast.error("Session expired. Please log in.");
        navigate("/admin");
        setLoading(false);
        return;
      }

      const results = await Promise.allSettled(
        endpoints.map(([url]) => api.get(url)),
      );
      results.forEach((res, idx) => {
        const [, setter] = endpoints[idx];
        if (res.status === "fulfilled") {
          try { setter(res.value.data); } catch { /* malformed response */ }
        }
      });
      // Lazy/optional fetches — also resilient.
      api.get("/admin/ugc/pending").then(r => setUgcItems(r.data || [])).catch(() => {});
      api.get("/admin/contracts").then(r => setContracts(r.data || [])).catch(() => {});
      api.get("/admin/behavior-insights").then(r => setBehaviorData(r.data)).catch(() => {});
      api.get("/admin/platform-settings").then(r => setPlatformSettings(r.data || {})).catch(() => {});
      setLoading(false);
    };
    fetchAll();
  }, [navigate, location.state]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch { /* silent */ }
    localStorage.removeItem("auth_token");
    navigate("/admin");
  };

  const togglePlatformSetting = async (key, value) => {
    setSavingSettings(true);
    // Optimistic UI — flip the flag immediately, roll back on failure.
    const prev = platformSettings;
    setPlatformSettings(s => ({ ...(s || {}), [key]: value }));
    try {
      await api.post("/admin/platform-settings", { [key]: value });
      toast.success(`${value ? "Enabled" : "Disabled"} ${key.replace(/_/g, " ")}.`);
    } catch {
      setPlatformSettings(prev);
      toast.error("Failed to save setting.");
    } finally {
      setSavingSettings(false);
    }
  };

  const loadSuggestionsTab = async () => {
    try {
      const r = await api.get("/admin/suggestions");
      setSuggestionsTabData({ items: r.data?.items || [], counts: r.data?.counts || {} });
    } catch { /* silent */ }
  };
  const loadSuggestionsDigest = async (force = false) => {
    setLoadingDigest(true);
    try {
      const r = await api.get(
        `/admin/suggestions/aria-weekly${force ? "?force=1" : ""}`,
        { timeout: 120000 },
      );
      setSuggestionsDigest(r.data);
    } catch { toast.error("Couldn't load ARIA digest."); }
    finally { setLoadingDigest(false); }
  };
  const runDeepDive = async () => {
    setLoadingDigest(true);
    // GPT-5 deep-dive can take 40-90s. Bump the per-request timeout
    // well above the axios default and keep the loading state on so
    // the user sees a clear "analyzing" cue.
    try {
      const r = await api.post(
        "/admin/suggestions/aria-analyse", null,
        { params: { days: 30 }, timeout: 120000 },
      );
      setSuggestionsDigest({ ...r.data, generated_at: new Date().toISOString(), cached: false, _deep: true });
      toast.success("Deep dive complete.");
    } catch { toast.error("Deep dive failed."); }
    finally { setLoadingDigest(false); }
  };
  const setSuggestionStatus = async (id, status) => {
    try {
      await api.post(`/admin/suggestions/${id}/status`, { status });
      toast.success(`Marked as ${status.replace("_", " ")}.`);
      await loadSuggestionsTab();
    } catch { toast.error("Couldn't update."); }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status } : b)));
      toast.success(`Booking ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const createGallery = async () => {
    if (!galleryForm.client_email || !galleryForm.title || !galleryForm.event_type || !galleryForm.event_date) {
      toast.error("Please fill all required fields."); return;
    }
    try {
      const res = await api.post("/admin/galleries", galleryForm);
      setGalleries((prev) => [res.data, ...prev]);
      setShowCreateGallery(false);
      setGalleryForm({ client_email: "", title: "", event_type: "", event_date: "", description: "" });
      toast.success("Gallery created successfully.");
    } catch {
      toast.error("Failed to create gallery.");
    }
  };

  const addMedia = async () => {
    if (!addMediaForm.gallery_id || !addMediaForm.url || !addMediaForm.filename) {
      toast.error("Please fill all required fields."); return;
    }
    try {
      await api.post(`/admin/galleries/${addMediaForm.gallery_id}/media`, {
        url: addMediaForm.url, filename: addMediaForm.filename, media_type: addMediaForm.media_type
      });
      setGalleries((prev) => prev.map((g) => g.id === addMediaForm.gallery_id ? { ...g, media_count: (g.media_count || 0) + 1 } : g));
      setAddMediaForm({ gallery_id: "", url: "", filename: "", media_type: "image" });
      toast.success("Media added to gallery.");
    } catch {
      toast.error("Failed to add media.");
    }
  };

  const deleteGallery = async (galleryId) => {
    try {
      const res = await api.delete(`/admin/galleries/${galleryId}`);
      // Editor deletes are intercepted — 202 + pending_approval flag means
      // the gallery is still there awaiting admin sign-off.
      if (res.status === 202 && res.data?.pending_approval) {
        toast.success(res.data.message || "Submitted for admin approval.");
        return;
      }
      setGalleries((prev) => prev.filter((g) => g.id !== galleryId));
      toast.success("Gallery deleted.");
    } catch {
      toast.error("Failed to delete gallery.");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadGalleryId) {
      toast.error("Please select a gallery first."); return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/admin/upload-to-gallery/${uploadGalleryId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setGalleries((prev) => prev.map((g) => g.id === uploadGalleryId ? { ...g, media_count: (g.media_count || 0) + 1 } : g));
      toast.success(`${file.name} uploaded and client notified.`);
      e.target.value = "";
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const updateSponsoredStatus = async (videoId, status) => {
    try {
      await api.patch(`/admin/sponsored-videos/${videoId}`, { status });
      setSponsoredVideos((prev) => prev.map((v) => v.id === videoId ? { ...v, status } : v));
      toast.success(`Video ${status}`);
    } catch { toast.error("Failed to update"); }
  };

  const deleteSponsoredVideo = async (videoId) => {
    try {
      await api.delete(`/admin/sponsored-videos/${videoId}`);
      setSponsoredVideos((prev) => prev.filter((v) => v.id !== videoId));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const createAffiliate = async () => {
    if (!affiliateForm.company_name || !affiliateForm.contact_email || !affiliateForm.slug) { toast.error("Fill required fields."); return; }
    try {
      const res = await api.post("/admin/affiliates", affiliateForm);
      setAffiliatesData((prev) => [res.data, ...prev]);
      setShowCreateAffiliate(false);
      setAffiliateForm({ company_name: "", contact_email: "", slug: "", commission_type: "percentage", commission_rate: 10, description: "" });
      toast.success("Affiliate created.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const createAffiliateLink = async () => {
    if (!affiliateLinkForm.affiliate_id || !affiliateLinkForm.title || !affiliateLinkForm.url) { toast.error("Fill required fields."); return; }
    try {
      const payload = { ...affiliateLinkForm, price: affiliateLinkForm.price ? parseFloat(affiliateLinkForm.price) : null };
      await api.post("/admin/affiliate-links", payload);
      setAffiliatesData((prev) => prev.map((a) => a.id === affiliateLinkForm.affiliate_id ? { ...a, links: [...(a.links || []), payload] } : a));
      setAffiliateLinkForm({ affiliate_id: "", title: "", description: "", url: "", image_url: "", product_type: "service", price: "" });
      toast.success("Link added.");
    } catch { toast.error("Failed"); }
  };

  const deleteAffiliate = async (id) => {
    try {
      await api.delete(`/admin/affiliates/${id}`);
      setAffiliatesData((prev) => prev.filter((a) => a.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setAllReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch { toast.error("Failed"); }
  };

  const createEvent = async () => {
    if (!eventForm.title) { toast.error("Title required."); return; }
    try {
      const tags = eventForm.tags ? eventForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
      const res = await api.post("/admin/events", { ...eventForm, tags });
      setAdminEvents(prev => [res.data, ...prev]);
      setShowCreateEvent(false);
      setEventForm({ title: "", description: "", event_type: "General", event_date: "", location: "", cover_image: "", tags: "" });
      toast.success("Event created and published.");
    } catch { toast.error("Failed."); }
  };

  const deleteEvent = async (id) => {
    try { await api.delete(`/admin/events/${id}`); setAdminEvents(prev => prev.filter(e => e.id !== id)); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  const addEventMediaUrl = async () => {
    if (!eventMediaForm.event_id || !eventMediaForm.url) { toast.error("Select event and provide URL."); return; }
    try {
      await api.post(`/admin/events/${eventMediaForm.event_id}/media-url`, { url: eventMediaForm.url, filename: eventMediaForm.filename || "media", media_type: eventMediaForm.media_type });
      setAdminEvents(prev => prev.map(e => e.id === eventMediaForm.event_id ? { ...e, media_count: (e.media_count || 0) + 1 } : e));
      setEventMediaForm({ event_id: "", url: "", filename: "", media_type: "image" });
      toast.success("Media added.");
    } catch { toast.error("Failed."); }
  };

  const handleEventUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !eventUploadId) { toast.error("Select an event first."); return; }
    setEventUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/admin/events/${eventUploadId}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAdminEvents(prev => prev.map(ev => ev.id === eventUploadId ? { ...ev, media_count: (ev.media_count || 0) + 1 } : ev));
      toast.success(`${file.name} uploaded to event.`);
      e.target.value = "";
    } catch { toast.error("Upload failed."); } finally { setEventUploading(false); }
  };

  const downloadQR = (eventId, title) => {
    const link = document.createElement("a");
    link.href = `${API}/events/${eventId}/qr-code`;
    link.download = `qr-${title.replace(/\s+/g, "-").toLowerCase()}.png`;
    link.click();
  };

  const viewCheckins = async (eventId) => {
    if (checkinEventId === eventId) { setCheckinEventId(null); return; }
    try {
      const res = await api.get(`/admin/events/${eventId}/checkins`);
      setCheckinList(res.data);
      setCheckinEventId(eventId);
    } catch { toast.error("Failed to load check-ins."); }
  };

  const viewTickets = async (eventId) => {
    if (ticketEventId === eventId) { setTicketEventId(null); return; }
    try {
      const res = await api.get(`/admin/events/${eventId}/tickets`);
      setTicketList(res.data);
      setTicketEventId(eventId);
    } catch { toast.error("Failed to load tickets."); }
  };

  const validateTicket = async (ticketId) => {
    try {
      const res = await api.post(`/admin/tickets/${ticketId}/validate`);
      if (res.data.valid) {
        toast.success(res.data.message);
        setTicketList(prev => prev.map(t => t.id === ticketId ? { ...t, used: true } : t));
      } else {
        toast.error(res.data.reason);
      }
    } catch { toast.error("Validation failed."); }
  };

  const generatePreview = async (eventId, type) => {
    setGeneratingPreview(true);
    setPreviewResult(null);
    try {
      const res = await api.post(`/admin/events/${eventId}/generate-preview`, { type });
      setPreviewResult({ eventId, ...res.data });
      toast.success("AI preview generated!");
    } catch { toast.error("Preview generation failed."); } finally { setGeneratingPreview(false); }
  };

  const reprocessAllImages = async () => {
    setReprocessing(true);
    try {
      const res = await api.post("/admin/images/reprocess-all");
      toast.success(res.data.message);
    } catch { toast.error("Reprocess failed."); } finally { setReprocessing(false); }
  };

  // ---- Media Management Functions ----
  const toggleGalleryMedia = async (galleryId) => {
    if (expandedGallery === galleryId) { setExpandedGallery(null); return; }
    try {
      const res = await api.get(`/admin/galleries/${galleryId}/media`);
      setGalleryMedia(res.data.media || []);
      setExpandedGallery(galleryId);
    } catch { toast.error("Failed to load media."); }
  };

  const deleteGalleryMediaItem = async (mediaId, galleryId) => {
    try {
      await api.delete(`/admin/gallery-media/${mediaId}`);
      setGalleryMedia(prev => prev.filter(m => m.id !== mediaId));
      setGalleries(prev => prev.map(g => g.id === galleryId ? { ...g, media_count: Math.max((g.media_count || 1) - 1, 0) } : g));
      toast.success("Media deleted.");
    } catch { toast.error("Failed to delete media."); }
  };

  const toggleEventMedia = async (eventId) => {
    if (expandedEventMedia === eventId) { setExpandedEventMedia(null); return; }
    try {
      const res = await api.get(`/admin/events/${eventId}/media`);
      setEventMediaList(res.data.media || []);
      setExpandedEventMedia(eventId);
    } catch { toast.error("Failed to load media."); }
  };

  const deleteEventMediaItem = async (mediaId, eventId) => {
    try {
      await api.delete(`/admin/event-media/${mediaId}`);
      setEventMediaList(prev => prev.filter(m => m.id !== mediaId));
      setAdminEvents(prev => prev.map(e => e.id === eventId ? { ...e, media_count: Math.max((e.media_count || 1) - 1, 0) } : e));
      toast.success("Media deleted.");
    } catch { toast.error("Failed to delete media."); }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <div className="text-gold font-body animate-pulse">Loading Dashboard...</div>
      </div>
    );
  }

  const TABS = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "contacts", label: "Messages", icon: MessageCircle },
    { id: "payments", label: "Payments", icon: DollarSign },
    { id: "galleries", label: "Galleries", icon: Image },
    { id: "events-mgmt", label: "Events", icon: CalendarDays },
    { id: "clients", label: "VIP Clients", icon: Users },
    { id: "sponsored", label: "Sponsored", icon: Video },
    { id: "affiliates", label: "Affiliates", icon: Handshake },
    { id: "reviews", label: "Reviews", icon: Eye },
    { id: "pipeline", label: "Pipeline", icon: Inbox },
    { id: "ugc", label: "UGC", icon: Camera },
    { id: "contracts", label: "Contracts", icon: FileText },
    { id: "instagram", label: "Instagram", icon: Instagram },
    { id: "referrals", label: "Referrals", icon: Gift },
    { id: "chauffeur", label: "Chauffeur", icon: Car },
    { id: "manual-payments", label: "Manual Payments", icon: Receipt },
    { id: "payouts", label: "Driver Payouts", icon: Wallet },
    { id: "heatmap", label: "Demand Heatmap", icon: Flame },
    { id: "support", label: "Support", icon: LifeBuoy },
    { id: "qr-codes", label: "QR Codes", icon: QrCode },
    { id: "watermark", label: "Watermark", icon: Shield },
    { id: "studio", label: "AI Studio", icon: Wand2 },
    { id: "client-errors", label: "Client Errors", icon: Bug },
    { id: "behavior", label: "Behavior AI", icon: TrendingUp },
    { id: "ai-intel", label: "AI Intelligence", icon: BarChart3 },
    { id: "suggestions", label: "Suggestions", icon: Lightbulb },
    { id: "settings", label: "Settings", icon: SlidersHorizontal },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Admin Dashboard</p>
            <h1 className="font-heading text-3xl text-white">Command Center</h1>
          </div>
          <div className="flex items-center gap-4">
            <ApprovalsBadge />
            <button data-testid="open-activity-log" onClick={() => navigate("/admin/activity")} className="flex items-center gap-2 text-white/50 hover:text-gold text-sm font-body transition-colors">
              <Activity size={16} /> Activity Log
            </button>
            <button data-testid="admin-logout" onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-body transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body whitespace-nowrap transition-all ${
                activeTab === tab.id ? "bg-gold/15 text-gold border border-gold/30" : "border border-white/10 text-white/50 hover:text-white"
              }`}
            >
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && analytics && (
          <motion.div initial="hidden" animate="visible">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Bookings", value: analytics.bookings.total, icon: CalendarDays, sub: `${analytics.bookings.pending} pending` },
                { label: "Revenue", value: `$${analytics.payments.revenue.toLocaleString()}`, icon: DollarSign, sub: `${analytics.payments.paid} paid` },
                { label: "Messages", value: analytics.contacts.total, icon: MessageCircle, sub: `${analytics.contacts.unread} unread` },
                { label: "AI Chats", value: analytics.chats.total, icon: Users, sub: "Total conversations" },
              ].map((stat, i) => (
                <motion.div key={stat.label} variants={fadeUp} custom={i} className="glass-card p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white/40 text-xs font-body uppercase tracking-wider">{stat.label}</span>
                    <stat.icon size={18} className="text-gold/60" />
                  </div>
                  <p className="text-white text-2xl font-heading font-light mb-1">{stat.value}</p>
                  <p className="text-white/30 text-xs font-body">{stat.sub}</p>
                </motion.div>
              ))}
            </div>

            {/* Recent Bookings */}
            <motion.div variants={fadeUp} custom={4} className="glass-card p-6">
              <h3 className="text-white font-body text-sm font-medium mb-4">Recent Bookings</h3>
              {analytics.recent_bookings.length === 0 ? (
                <p className="text-white/40 text-sm font-body">No bookings yet.</p>
              ) : (
                <div className="space-y-3">
                  {analytics.recent_bookings.map((b) => (
                    <div key={b.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white text-sm font-body">{b.name}</p>
                        <p className="text-white/40 text-xs font-body">{b.event_type} - {b.event_date}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-body ${
                        b.status === "confirmed" ? "bg-green-500/15 text-green-400" :
                        b.status === "pending" ? "bg-gold/15 text-gold" : "bg-red-500/15 text-red-400"
                      }`}>{b.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <motion.div initial="hidden" animate="visible" className="space-y-4">
            {bookings.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No bookings yet.</p>
            ) : bookings.map((b, i) => (
              <motion.div key={b.id} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`booking-card-${b.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-body font-medium text-sm">{b.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-body ${
                        b.status === "confirmed" ? "bg-green-500/15 text-green-400" :
                        b.status === "pending" ? "bg-gold/15 text-gold" : "bg-red-500/15 text-red-400"
                      }`}>{b.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-white/40 text-xs font-body">
                      <span>{b.email}</span>
                      <span>{b.event_type}</span>
                      <span>{b.event_date}</span>
                      <span>{b.location}</span>
                      {b.budget && <span>{b.budget}</span>}
                    </div>
                    {b.services?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {b.services.map((s) => (
                          <span key={s} className="px-2 py-0.5 text-xs border border-white/10 text-white/50 font-body">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateBookingStatus(b.id, "confirmed")} className="px-4 py-2 bg-green-500/15 text-green-400 text-xs font-body hover:bg-green-500/25 transition-colors">
                      <CheckCircle size={14} className="inline mr-1" /> Confirm
                    </button>
                    <button onClick={() => updateBookingStatus(b.id, "declined")} className="px-4 py-2 bg-red-500/15 text-red-400 text-xs font-body hover:bg-red-500/25 transition-colors">
                      Decline
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <motion.div initial="hidden" animate="visible" className="space-y-4">
            {contacts.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No messages yet.</p>
            ) : contacts.map((c, i) => (
              <motion.div key={c.id} variants={fadeUp} custom={i} className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-body font-medium text-sm">{c.name}</h3>
                  {c.status === "unread" && <span className="w-2 h-2 bg-gold rounded-full" />}
                </div>
                <p className="text-gold text-xs font-body mb-1">{c.subject}</p>
                <p className="text-white/60 text-sm font-body font-light mb-3">{c.message}</p>
                <div className="flex gap-4 text-white/30 text-xs font-body">
                  <span>{c.email}</span>
                  {c.phone && <span>{c.phone}</span>}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Payments Tab */}
        {activeTab === "payments" && (
          <motion.div initial="hidden" animate="visible" className="space-y-4">
            {payments.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No transactions yet.</p>
            ) : payments.map((p, i) => (
              <motion.div key={p.id || i} variants={fadeUp} custom={i} className="glass-card p-6 flex items-center justify-between">
                <div>
                  <p className="text-white font-body text-sm font-medium">{p.package_name}</p>
                  <p className="text-white/40 text-xs font-body mt-1">Session: {p.session_id?.slice(0, 20)}...</p>
                </div>
                <div className="text-right">
                  <p className="text-gold font-body font-medium">${p.amount?.toLocaleString()}</p>
                  <span className={`text-xs font-body ${
                    p.payment_status === "paid" ? "text-green-400" : "text-white/40"
                  }`}>{p.payment_status}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Galleries Tab */}
        {activeTab === "galleries" && (
          <motion.div initial="hidden" animate="visible">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-body text-sm font-medium">Client Galleries ({galleries.length})</h3>
              <button
                data-testid="create-gallery-btn"
                onClick={() => setShowCreateGallery(!showCreateGallery)}
                className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors"
              >
                <Plus size={14} /> New Gallery
              </button>
            </div>

            {showCreateGallery && (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-6 mb-6 space-y-4">
                <h4 className="text-white font-body text-sm font-medium">Create Gallery</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Client Email *</label>
                    <Input data-testid="gallery-client-email" value={galleryForm.client_email} onChange={(e) => setGalleryForm({...galleryForm, client_email: e.target.value})} placeholder="client@email.com" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Title *</label>
                    <Input data-testid="gallery-title-input" value={galleryForm.title} onChange={(e) => setGalleryForm({...galleryForm, title: e.target.value})} placeholder="Event name" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Event Type *</label>
                    <Input data-testid="gallery-event-type" value={galleryForm.event_type} onChange={(e) => setGalleryForm({...galleryForm, event_type: e.target.value})} placeholder="e.g. Wedding" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Event Date *</label>
                    <Input data-testid="gallery-event-date" type="date" value={galleryForm.event_date} onChange={(e) => setGalleryForm({...galleryForm, event_date: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Description</label>
                  <Input data-testid="gallery-description" value={galleryForm.description} onChange={(e) => setGalleryForm({...galleryForm, description: e.target.value})} placeholder="Optional description" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                </div>
                <button data-testid="submit-create-gallery" onClick={createGallery} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors">Create Gallery</button>
              </motion.div>
            )}

            {/* Add Media Form */}
            <motion.div variants={fadeUp} custom={1} className="glass-card p-6 mb-6 space-y-4">
              <h4 className="text-white font-body text-sm font-medium">Add Media to Gallery (via URL)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Gallery</label>
                  <Select value={addMediaForm.gallery_id} onValueChange={(v) => setAddMediaForm({...addMediaForm, gallery_id: v})}>
                    <SelectTrigger data-testid="media-gallery-select" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm">
                      <SelectValue placeholder="Select gallery" />
                    </SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">
                      {galleries.map((g) => (
                        <SelectItem key={g.id} value={g.id} className="text-white/80 focus:bg-gold/10 text-sm">{g.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Image URL</label>
                  <Input data-testid="media-url-input" value={addMediaForm.url} onChange={(e) => setAddMediaForm({...addMediaForm, url: e.target.value})} placeholder="https://..." className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Filename</label>
                  <Input data-testid="media-filename-input" value={addMediaForm.filename} onChange={(e) => setAddMediaForm({...addMediaForm, filename: e.target.value})} placeholder="photo-01.jpg" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                </div>
                <div className="flex items-end">
                  <button data-testid="submit-add-media" onClick={addMedia} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors h-10">Add via URL</button>
                </div>
              </div>
            </motion.div>

            {/* Direct File Upload */}
            <motion.div variants={fadeUp} custom={2} className="glass-card p-6 mb-6 space-y-4">
              <h4 className="text-white font-body text-sm font-medium flex items-center gap-2"><Upload size={16} className="text-gold" /> Upload Media Files</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Gallery *</label>
                  <Select value={uploadGalleryId} onValueChange={setUploadGalleryId}>
                    <SelectTrigger data-testid="upload-gallery-select" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm">
                      <SelectValue placeholder="Select gallery" />
                    </SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">
                      {galleries.map((g) => (
                        <SelectItem key={g.id} value={g.id} className="text-white/80 focus:bg-gold/10 text-sm">{g.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Choose File (max 20MB)</label>
                  <input
                    data-testid="file-upload-input"
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    disabled={uploading || !uploadGalleryId}
                    className="block w-full text-xs text-white/50 file:mr-3 file:py-2 file:px-4 file:bg-gold/15 file:text-gold file:border-0 file:text-xs file:font-body file:cursor-pointer hover:file:bg-gold/25 disabled:opacity-40"
                  />
                </div>
                <div>
                  {uploading && <p className="text-gold text-xs font-body animate-pulse">Uploading...</p>}
                </div>
              </div>
              <p className="text-white/20 text-xs font-body">Uploaded files are stored securely and the client will be automatically notified.</p>
            </motion.div>

            {/* Gallery List */}
            <div className="space-y-4">
              {galleries.map((g, i) => (
                <motion.div key={g.id} variants={fadeUp} custom={i + 2} className="glass-card p-6" data-testid={`admin-gallery-${g.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 cursor-pointer" onClick={() => toggleGalleryMedia(g.id)}>
                      <h4 className="text-white font-body text-sm font-medium">{g.title}</h4>
                      <div className="flex gap-4 text-white/40 text-xs font-body mt-1">
                        <span>{g.client_email}</span>
                        <span>{g.event_type}</span>
                        <span>{g.event_date}</span>
                        <span className="text-gold/60">{g.media_count || 0} media items</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Link to={`/admin/gallery/${g.id}`} className="text-gold/50 hover:text-gold text-xs font-body transition-colors" data-testid={`manage-gallery-${g.id}`}>Manage</Link>
                      <button onClick={() => toggleGalleryMedia(g.id)} className="text-white/30 hover:text-gold transition-colors" data-testid={`expand-gallery-${g.id}`}>
                        {expandedGallery === g.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <button onClick={() => deleteGallery(g.id)} className="text-red-400/60 hover:text-red-400 transition-colors" data-testid={`delete-gallery-${g.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {expandedGallery === g.id && (
                    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`gallery-media-grid-${g.id}`}>
                      <h5 className="text-white/60 font-body text-xs font-medium mb-3 flex items-center gap-2"><Image size={14} className="text-gold/60" /> Media ({galleryMedia.length})</h5>
                      {galleryMedia.length === 0 ? (
                        <p className="text-white/30 text-xs font-body">No media items yet. Use the forms above to add media.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {galleryMedia.map(m => (
                            <div key={m.id} className="group relative aspect-square bg-white/5 overflow-hidden" data-testid={`gallery-media-${m.id}`}>
                              {m.media_type === "image" ? (
                                <img src={m.url} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><Video size={24} className="text-white/30" /></div>
                              )}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                {m.media_type === "image" && (
                                  <button onClick={() => setLightboxImg(m.url)} className="p-2 bg-white/10 hover:bg-gold/30 rounded transition-colors" data-testid={`zoom-gallery-media-${m.id}`}><ZoomIn size={14} className="text-white" /></button>
                                )}
                                <button onClick={() => deleteGalleryMediaItem(m.id, g.id)} className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded transition-colors" data-testid={`delete-gallery-media-${m.id}`}><Trash2 size={14} className="text-red-400" /></button>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                <p className="text-white/70 text-[9px] font-body truncate">{m.filename}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Events Management Tab */}
        {activeTab === "events-mgmt" && (
          <motion.div initial="hidden" animate="visible">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-body text-sm font-medium">Events ({adminEvents.length}) — Unlimited pages, no restrictions</h3>
              <div className="flex items-center gap-3">
                <button data-testid="reprocess-images-btn" onClick={reprocessAllImages} disabled={reprocessing} className="flex items-center gap-2 bg-white/5 text-white/50 px-4 py-2 text-xs font-body hover:text-gold hover:bg-gold/10 transition-colors disabled:opacity-40">
                  <RotateCw size={14} className={reprocessing ? "animate-spin" : ""} /> {reprocessing ? "Processing..." : "Reprocess All Images"}
                </button>
                <button data-testid="create-event-btn" onClick={() => setShowCreateEvent(!showCreateEvent)} className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors"><Plus size={14} /> New Event</button>
              </div>
            </div>

            {showCreateEvent && (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-6 mb-6 space-y-4">
                <h4 className="text-white font-body text-sm font-medium">Create Event Page</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Event Title *</label><Input data-testid="event-create-title" value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="Event name" className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" /></div>
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Event Type</label>
                    <Select value={eventForm.event_type} onValueChange={v => setEventForm({...eventForm, event_type: v})}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-luxury-charcoal border-white/10">
                        {["Wedding", "Corporate", "Yacht & Sea", "Nightlife", "Concert", "Festival", "Private Party", "Product Launch", "Charity Gala", "General"].map(t => <SelectItem key={t} value={t} className="text-white/80 text-sm">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Date</label><Input data-testid="event-create-date" type="date" value={eventForm.event_date} onChange={e => setEventForm({...eventForm, event_date: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" /></div>
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Location</label><Input value={eventForm.location} onChange={e => setEventForm({...eventForm, location: e.target.value})} placeholder="Venue, city" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Cover Image URL</label><Input value={eventForm.cover_image} onChange={e => setEventForm({...eventForm, cover_image: e.target.value})} placeholder="https://..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
                  <div><label className="text-white/40 text-xs font-body mb-1 block">Tags (comma-separated)</label><Input value={eventForm.tags} onChange={e => setEventForm({...eventForm, tags: e.target.value})} placeholder="VIP, beach, wedding" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
                </div>
                <div><label className="text-white/40 text-xs font-body mb-1 block">Description</label><Input value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="Event description" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" /></div>
                <button data-testid="submit-create-event" onClick={createEvent} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors">Publish Event</button>
              </motion.div>
            )}

            {/* Add Media via URL */}
            <motion.div variants={fadeUp} custom={1} className="glass-card p-6 mb-4 space-y-4">
              <h4 className="text-white font-body text-sm font-medium">Add Media via URL</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div><label className="text-white/40 text-xs font-body mb-1 block">Event</label>
                  <Select value={eventMediaForm.event_id} onValueChange={v => setEventMediaForm({...eventMediaForm, event_id: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">{adminEvents.map(e => <SelectItem key={e.id} value={e.id} className="text-white/80 text-xs">{e.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-white/40 text-xs font-body mb-1 block">URL</label><Input value={eventMediaForm.url} onChange={e => setEventMediaForm({...eventMediaForm, url: e.target.value})} placeholder="https://..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-xs" /></div>
                <div><label className="text-white/40 text-xs font-body mb-1 block">Filename</label><Input value={eventMediaForm.filename} onChange={e => setEventMediaForm({...eventMediaForm, filename: e.target.value})} placeholder="photo.jpg" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-xs" /></div>
                <div><label className="text-white/40 text-xs font-body mb-1 block">Type</label>
                  <Select value={eventMediaForm.media_type} onValueChange={v => setEventMediaForm({...eventMediaForm, media_type: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10"><SelectItem value="image" className="text-white/80 text-xs">Image</SelectItem><SelectItem value="video" className="text-white/80 text-xs">Video</SelectItem></SelectContent>
                  </Select>
                </div>
                <button onClick={addEventMediaUrl} className="bg-gold text-black px-4 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors h-10">Add</button>
              </div>
            </motion.div>

            {/* Direct Upload */}
            <motion.div variants={fadeUp} custom={2} className="glass-card p-6 mb-6 space-y-4">
              <h4 className="text-white font-body text-sm font-medium flex items-center gap-2"><Upload size={16} className="text-gold" /> Upload Files Directly</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div><label className="text-white/40 text-xs font-body mb-1 block">Event *</label>
                  <Select value={eventUploadId} onValueChange={setEventUploadId}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-xs"><SelectValue placeholder="Select event" /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">{adminEvents.map(e => <SelectItem key={e.id} value={e.id} className="text-white/80 text-xs">{e.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-white/40 text-xs font-body mb-1 block">File (photo/video, max 20MB)</label>
                  <input type="file" accept="image/*,video/*" onChange={handleEventUpload} disabled={eventUploading || !eventUploadId} className="block w-full text-xs text-white/50 file:mr-3 file:py-2 file:px-4 file:bg-gold/15 file:text-gold file:border-0 file:text-xs file:cursor-pointer disabled:opacity-40" />
                </div>
                {eventUploading && <p className="text-gold text-xs font-body animate-pulse">Uploading...</p>}
              </div>
            </motion.div>

            {/* Event List */}
            <div className="space-y-4">
              {adminEvents.map((ev, i) => (
                <motion.div key={ev.id} variants={fadeUp} custom={i + 3} className="glass-card p-6" data-testid={`admin-event-${ev.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-white font-body text-sm font-medium">{ev.title}</h4>
                        <span className="px-2 py-0.5 text-[9px] bg-gold/10 text-gold font-body">{ev.event_type}</span>
                        <span className="px-2 py-0.5 text-[9px] bg-green-500/15 text-green-400 font-body">{ev.status}</span>
                      </div>
                      <div className="flex gap-3 text-white/30 text-xs font-body">{ev.event_date && <span>{ev.event_date}</span>}{ev.location && <span>{ev.location}</span>}<span>{ev.media_count || 0} media</span><span className="text-gold/50">{ev.attendance || 0} check-ins</span></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleEventMedia(ev.id)} data-testid={`view-media-${ev.id}`} className={`transition-colors ${expandedEventMedia === ev.id ? "text-gold" : "text-white/30 hover:text-gold/60"}`} title="View Media"><Image size={16} /></button>
                      <button onClick={() => downloadQR(ev.id, ev.title)} data-testid={`qr-download-${ev.id}`} className="text-gold/50 hover:text-gold transition-colors" title="Download QR Code"><QrCode size={16} /></button>
                      <button onClick={() => viewCheckins(ev.id)} data-testid={`view-checkins-${ev.id}`} className={`transition-colors ${checkinEventId === ev.id ? "text-gold" : "text-white/30 hover:text-gold/60"}`} title="View Check-ins"><Users size={16} /></button>
                      <button onClick={() => viewTickets(ev.id)} data-testid={`view-tickets-${ev.id}`} className={`transition-colors ${ticketEventId === ev.id ? "text-gold" : "text-white/30 hover:text-gold/60"}`} title="View Tickets"><Ticket size={16} /></button>
                      <Link to={`/admin/events/${ev.id}/analytics`} data-testid={`event-analytics-${ev.id}`} className="text-white/30 hover:text-gold/60 transition-colors" title="Analytics"><BarChart3 size={16} /></Link>
                      <button onClick={() => generatePreview(ev.id, "description")} disabled={generatingPreview} data-testid={`ai-preview-${ev.id}`} className="text-white/30 hover:text-gold/60 transition-colors disabled:opacity-30" title="AI Generate Preview"><Sparkles size={16} /></button>
                      <button onClick={() => deleteEvent(ev.id)} className="text-red-400/60 hover:text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {checkinEventId === ev.id && (
                    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`checkin-list-${ev.id}`}>
                      <h5 className="text-white/60 font-body text-xs font-medium mb-3">Check-in List ({checkinList.length})</h5>
                      {checkinList.length === 0 ? <p className="text-white/30 text-xs font-body">No check-ins yet.</p> : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {checkinList.map(c => (
                            <div key={c.id} className="flex items-center justify-between bg-white/[0.02] px-4 py-2">
                              <div>
                                <span className="text-white/70 text-xs font-body">{c.guest_name}</span>
                                <span className="text-white/30 text-xs font-body ml-3">{c.guest_email}</span>
                              </div>
                              <span className="text-white/20 text-[10px] font-body">{c.checked_in_at ? new Date(c.checked_in_at).toLocaleString() : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {ticketEventId === ev.id && (
                    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`ticket-list-${ev.id}`}>
                      <h5 className="text-white/60 font-body text-xs font-medium mb-3">Tickets ({ticketList.length})</h5>
                      {ticketList.length === 0 ? <p className="text-white/30 text-xs font-body">No tickets yet.</p> : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {ticketList.map(t => (
                            <div key={t.id} className="flex items-center justify-between bg-white/[0.02] px-4 py-2">
                              <div>
                                <span className="text-white/70 text-xs font-body">{t.buyer_name}</span>
                                <span className="text-white/30 text-xs font-body ml-3">{t.ticket_type_name} x{t.quantity}</span>
                                <span className={`text-[10px] font-body ml-3 ${t.payment_status === "paid" ? "text-green-400" : "text-yellow-400"}`}>{t.payment_status}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {t.payment_status === "paid" && !t.used && (
                                  <button onClick={() => validateTicket(t.id)} className="text-gold/60 hover:text-gold text-[10px] font-body px-2 py-1 border border-gold/20 hover:border-gold/40 transition-colors">Validate</button>
                                )}
                                {t.used && <span className="text-white/20 text-[10px] font-body">Used</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {previewResult?.eventId === ev.id && (
                    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`preview-result-${ev.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-gold/70 font-body text-xs font-medium">AI Generated ({previewResult.type})</h5>
                        <div className="flex gap-2">
                          <button onClick={() => generatePreview(ev.id, "description")} className="text-[10px] font-body px-2 py-1 border border-white/10 text-white/40 hover:border-gold/30 hover:text-gold transition-colors">Description</button>
                          <button onClick={() => generatePreview(ev.id, "social")} className="text-[10px] font-body px-2 py-1 border border-white/10 text-white/40 hover:border-gold/30 hover:text-gold transition-colors">Social</button>
                          <button onClick={() => generatePreview(ev.id, "email")} className="text-[10px] font-body px-2 py-1 border border-white/10 text-white/40 hover:border-gold/30 hover:text-gold transition-colors">Email</button>
                        </div>
                      </div>
                      <div className="bg-white/[0.02] p-4 text-white/70 text-xs font-body leading-relaxed whitespace-pre-wrap">{previewResult.preview}</div>
                    </div>
                  )}
                  {expandedEventMedia === ev.id && (
                    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`event-media-grid-${ev.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-white/60 font-body text-xs font-medium flex items-center gap-2"><Image size={14} className="text-gold/60" /> Event Media ({eventMediaList.length})</h5>
                        <button onClick={async () => { toast.info("Reprocessing..."); try { const res = await api.post(`/admin/events/${ev.id}/reprocess-media`); toast.success(res.data.message); toggleEventMedia(ev.id); } catch { toast.error("Reprocess failed"); }}} className="text-[10px] font-body text-white/30 hover:text-gold flex items-center gap-1 transition-colors" data-testid={`reprocess-event-${ev.id}`}><RotateCw size={10} /> Fix Orientation</button>
                      </div>
                      {eventMediaList.length === 0 ? (
                        <p className="text-white/30 text-xs font-body">No media items yet. Use the upload forms above to add media to this event.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                          {eventMediaList.map(m => (
                            <div key={m.id} className="group relative aspect-square bg-white/5 overflow-hidden" data-testid={`event-media-${m.id}`}>
                              {m.media_type === "image" ? (
                                <img src={m.url} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><Video size={24} className="text-white/30" /></div>
                              )}
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                {m.media_type === "image" && (
                                  <button onClick={() => setLightboxImg(m.url)} className="p-2 bg-white/10 hover:bg-gold/30 rounded transition-colors" data-testid={`zoom-event-media-${m.id}`}><ZoomIn size={14} className="text-white" /></button>
                                )}
                                <button onClick={() => deleteEventMediaItem(m.id, ev.id)} className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded transition-colors" data-testid={`delete-event-media-${m.id}`}><Trash2 size={14} className="text-red-400" /></button>
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                <p className="text-white/70 text-[9px] font-body truncate">{m.filename}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Clients Tab */}
        {activeTab === "clients" && (
          <motion.div initial="hidden" animate="visible" className="space-y-4">
            <h3 className="text-white font-body text-sm font-medium mb-4">VIP Clients ({clients.length})</h3>
            {clients.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No registered clients yet.</p>
            ) : clients.map((c, i) => (
              <motion.div key={c.email} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`client-card-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-body text-sm font-medium">{c.name}</h4>
                    <div className="flex gap-4 text-white/40 text-xs font-body mt-1">
                      <span>{c.email}</span>
                      {c.phone && <span>{c.phone}</span>}
                      <span>Joined: {c.created_at ? new Date(c.created_at).toLocaleDateString() : "N/A"}</span>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-gold/10 text-gold text-xs font-body">VIP</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Sponsored Videos Tab */}
        {activeTab === "sponsored" && (
          <motion.div initial="hidden" animate="visible">
            <h3 className="text-white font-body text-sm font-medium mb-4">Sponsored Videos ({sponsoredVideos.length})</h3>
            {sponsoredVideos.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No sponsored videos yet. Partners can submit videos at /partner</p>
            ) : (
              <div className="space-y-4">
                {sponsoredVideos.map((v, i) => (
                  <motion.div key={v.id} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`admin-sponsored-${v.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-body text-sm font-medium">{v.title}</h4>
                          <span className={`px-2 py-0.5 text-[10px] font-body uppercase ${v.status === "active" ? "bg-green-500/15 text-green-400" : v.status === "pending_payment" ? "bg-gold/15 text-gold" : "bg-white/10 text-white/40"}`}>{v.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-white/30 text-xs font-body mt-1">
                          <span>{v.company_name}</span>
                          <span>{v.company_email}</span>
                          <span>{v.tier} tier</span>
                          <span>${v.total_cost?.toLocaleString()}</span>
                          <span>{v.paid ? "Paid" : "Unpaid"}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {v.status !== "active" && v.paid && (
                          <button onClick={() => updateSponsoredStatus(v.id, "active")} className="px-3 py-1 bg-green-500/15 text-green-400 text-xs font-body">Activate</button>
                        )}
                        {v.status === "active" && (
                          <button onClick={() => updateSponsoredStatus(v.id, "paused")} className="px-3 py-1 bg-gold/15 text-gold text-xs font-body">Pause</button>
                        )}
                        <button onClick={() => deleteSponsoredVideo(v.id)} className="text-red-400/60 hover:text-red-400"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Affiliates Tab */}
        {activeTab === "affiliates" && (
          <motion.div initial="hidden" animate="visible">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-body text-sm font-medium">Affiliates ({affiliatesData.length})</h3>
              <button data-testid="create-affiliate-btn" onClick={() => setShowCreateAffiliate(!showCreateAffiliate)} className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors">
                <Plus size={14} /> New Affiliate
              </button>
            </div>

            {showCreateAffiliate && (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-6 mb-6 space-y-4">
                <h4 className="text-white font-body text-sm font-medium">Create Affiliate Partner</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Company Name *</label>
                    <Input data-testid="aff-company" value={affiliateForm.company_name} onChange={(e) => setAffiliateForm({...affiliateForm, company_name: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Email *</label>
                    <Input data-testid="aff-email" value={affiliateForm.contact_email} onChange={(e) => setAffiliateForm({...affiliateForm, contact_email: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">URL Slug * (e.g. company-name)</label>
                    <Input data-testid="aff-slug" value={affiliateForm.slug} onChange={(e) => setAffiliateForm({...affiliateForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Commission Type</label>
                    <Select value={affiliateForm.commission_type} onValueChange={(v) => setAffiliateForm({...affiliateForm, commission_type: v})}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-luxury-charcoal border-white/10">
                        <SelectItem value="percentage" className="text-white/80 text-sm">Percentage per sale</SelectItem>
                        <SelectItem value="flat" className="text-white/80 text-sm">Flat fee per click</SelectItem>
                        <SelectItem value="both" className="text-white/80 text-sm">Both (% + flat)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Rate ({affiliateForm.commission_type === "percentage" ? "%" : "$"})</label>
                    <Input data-testid="aff-rate" type="number" value={affiliateForm.commission_rate} onChange={(e) => setAffiliateForm({...affiliateForm, commission_rate: parseFloat(e.target.value) || 0})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="text-white/40 text-xs font-body mb-1 block">Description</label>
                    <Input value={affiliateForm.description} onChange={(e) => setAffiliateForm({...affiliateForm, description: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                  </div>
                </div>
                <button data-testid="submit-affiliate" onClick={createAffiliate} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors">Create Affiliate</button>
              </motion.div>
            )}

            {/* Add Link Form */}
            <motion.div variants={fadeUp} custom={1} className="glass-card p-6 mb-6 space-y-4">
              <h4 className="text-white font-body text-sm font-medium flex items-center gap-2"><LinkIcon size={14} className="text-gold" /> Add Affiliate Link</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Affiliate</label>
                  <Select value={affiliateLinkForm.affiliate_id} onValueChange={(v) => setAffiliateLinkForm({...affiliateLinkForm, affiliate_id: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue placeholder="Select affiliate" /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">
                      {affiliatesData.map((a) => <SelectItem key={a.id} value={a.id} className="text-white/80 text-sm">{a.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Title *</label>
                  <Input value={affiliateLinkForm.title} onChange={(e) => setAffiliateLinkForm({...affiliateLinkForm, title: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Destination URL *</label>
                  <Input value={affiliateLinkForm.url} onChange={(e) => setAffiliateLinkForm({...affiliateLinkForm, url: e.target.value})} placeholder="https://..." className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Image URL</label>
                  <Input value={affiliateLinkForm.image_url} onChange={(e) => setAffiliateLinkForm({...affiliateLinkForm, image_url: e.target.value})} className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body mb-1 block">Type</label>
                  <Select value={affiliateLinkForm.product_type} onValueChange={(v) => setAffiliateLinkForm({...affiliateLinkForm, product_type: v})}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-luxury-charcoal border-white/10">
                      <SelectItem value="service" className="text-white/80 text-sm">Service</SelectItem>
                      <SelectItem value="merchandise" className="text-white/80 text-sm">Merchandise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <button onClick={createAffiliateLink} className="bg-gold text-black px-6 py-2 text-xs font-body font-medium hover:bg-gold-light transition-colors h-10">Add Link</button>
                </div>
              </div>
            </motion.div>

            {/* Affiliate List */}
            <div className="space-y-4">
              {affiliatesData.map((aff, i) => (
                <motion.div key={aff.id} variants={fadeUp} custom={i + 2} className="glass-card p-6" data-testid={`admin-affiliate-${aff.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-white font-body text-sm font-medium">{aff.company_name}</h4>
                      <div className="flex gap-3 text-white/30 text-xs font-body mt-1">
                        <span>{aff.contact_email}</span>
                        <span>/{aff.slug}</span>
                        <span>{aff.commission_type}: {aff.commission_rate}{aff.commission_type === "percentage" ? "%" : "$"}</span>
                        <span>{aff.total_clicks || 0} clicks</span>
                      </div>
                    </div>
                    <button onClick={() => deleteAffiliate(aff.id)} className="text-red-400/60 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                  {aff.links?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {aff.links.map((l) => (
                        <span key={l.id || l.title} className="px-2 py-1 text-[10px] border border-white/10 text-white/50 font-body">{l.title} ({l.clicks || 0} clicks)</span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <motion.div initial="hidden" animate="visible">
            <h3 className="text-white font-body text-sm font-medium mb-4">All Reviews ({allReviews.length})</h3>
            {allReviews.length === 0 ? (
              <p className="text-white/40 text-sm font-body">No reviews yet.</p>
            ) : (
              <div className="space-y-4">
                {allReviews.map((r, i) => (
                  <motion.div key={r.id} variants={fadeUp} custom={i} className="glass-card p-5" data-testid={`admin-review-${r.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-body text-sm font-medium">{r.reviewer_name}</span>
                          <span className="text-gold text-xs font-body">{r.overall_rating}/5</span>
                          {r.consent_marketing && <span className="px-1.5 py-0.5 text-[9px] bg-green-500/15 text-green-400 font-body">opted-in</span>}
                        </div>
                        <div className="flex flex-wrap gap-3 text-white/30 text-[10px] font-body mb-2">
                          <span>{r.reviewer_email}</span>
                          {r.ticketing_rating && <span>Ticket: {r.ticketing_rating}/5</span>}
                          {r.ambiance_rating && <span>Ambiance: {r.ambiance_rating}/5</span>}
                          {r.bathroom_rating && <span>Bath: {r.bathroom_rating}/5</span>}
                          {r.food_drink_rating && <span>F&D: {r.food_drink_rating}/5</span>}
                        </div>
                        {r.liked && <p className="text-white/50 text-xs font-body mb-1"><span className="text-gold">Liked:</span> {r.liked}</p>}
                        {r.improve && <p className="text-white/40 text-xs font-body"><span className="text-white/50">Improve:</span> {r.improve}</p>}
                      </div>
                      <button onClick={() => deleteReview(r.id)} className="text-red-400/60 hover:text-red-400 flex-shrink-0"><Trash2 size={14} /></button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* AI Intelligence Tab */}
        {activeTab === "ai-intel" && (
          <motion.div initial="hidden" animate="visible">
            <h3 className="text-white font-body text-sm font-medium mb-6">ARIA Intelligence Dashboard</h3>
            <p className="text-white/40 font-body text-xs mb-8">All reviews, suggestions, and conversations feed into ARIA's intelligence system to improve marketing, services, and customer experience.</p>

            {/* Widget renders independently — it has its own data source and graceful empty/error states */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <TopAiTools />
              <TopCtaClicks />
            </div>

            {!aiInsights ? (
              <p data-testid="ai-insights-loading" className="text-white/30 font-body text-xs py-8">Loading ARIA insights…</p>
            ) : (
              <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Reviews", value: aiInsights.total_reviews },
                { label: "Total Suggestions", value: aiInsights.total_suggestions },
                { label: "Avg Overall Rating", value: `${aiInsights.avg_ratings.overall}/5` },
                { label: "Marketing Subscribers", value: aiInsights.marketing_subscribers },
              ].map((s, i) => (
                <motion.div key={s.label} variants={fadeUp} custom={i} className="glass-card p-5">
                  <p className="text-white/40 text-xs font-body uppercase tracking-wider mb-1">{s.label}</p>
                  <p className="text-gold font-heading text-2xl">{s.value}</p>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <motion.div variants={fadeUp} custom={4} className="glass-card p-6">
                <h4 className="text-white font-body text-sm font-medium mb-4">Rating Breakdown</h4>
                <div className="space-y-3">
                  {[
                    { label: "Overall", value: aiInsights.avg_ratings.overall },
                    { label: "Ticketing", value: aiInsights.avg_ratings.ticketing },
                    { label: "Ambiance", value: aiInsights.avg_ratings.ambiance },
                    { label: "Food & Drink", value: aiInsights.avg_ratings.food_drink },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center justify-between">
                      <span className="text-white/50 text-xs font-body">{r.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${(r.value / 5) * 100}%` }} /></div>
                        <span className="text-gold text-xs font-body w-8 text-right">{r.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={fadeUp} custom={5} className="glass-card p-6">
                <h4 className="text-white font-body text-sm font-medium mb-4">What Guests Love</h4>
                {aiInsights.liked_feedback.length === 0 ? <p className="text-white/30 text-xs font-body">No feedback yet.</p> : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {aiInsights.liked_feedback.map((f, i) => (
                      <p key={i} className="text-white/50 text-xs font-body leading-relaxed border-l-2 border-gold/30 pl-3">"{f}"</p>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div variants={fadeUp} custom={6} className="glass-card p-6">
                <h4 className="text-white font-body text-sm font-medium mb-4">Improvement Areas</h4>
                {aiInsights.improve_feedback.length === 0 ? <p className="text-white/30 text-xs font-body">No feedback yet.</p> : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {aiInsights.improve_feedback.map((f, i) => (
                      <p key={i} className="text-white/40 text-xs font-body leading-relaxed border-l-2 border-white/10 pl-3">"{f}"</p>
                    ))}
                  </div>
                )}
              </motion.div>

              <motion.div variants={fadeUp} custom={7} className="glass-card p-6">
                <h4 className="text-white font-body text-sm font-medium mb-4">Recent Suggestions ({allSuggestions.length})</h4>
                {allSuggestions.length === 0 ? <p className="text-white/30 text-xs font-body">No suggestions yet.</p> : (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {allSuggestions.slice(0, 10).map((s, i) => (
                      <div key={s.id || i} className="py-2 border-b border-white/5 last:border-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gold text-[10px] font-body uppercase">{s.category}</span>
                          <span className="text-white/20 text-[10px] font-body">{s.name}</span>
                        </div>
                        <p className="text-white/50 text-xs font-body">{s.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
              </>
            )}
          </motion.div>
        )}

        {/* Pipeline Tab */}
        {activeTab === "pipeline" && (
          <PipelineDashboardInline />
        )}

        {/* UGC Moderation Tab */}
        {activeTab === "ugc" && (
          <motion.div initial="hidden" animate="visible" data-testid="ugc-tab">
            <motion.h3 variants={fadeUp} custom={0} className="text-white font-heading text-xl mb-6">User-Generated Content</motion.h3>
            <motion.div variants={fadeUp} custom={1} className="glass-card p-6">
              <p className="text-white/60 text-xs font-body mb-4">Pending Guest Uploads ({ugcItems.length})</p>
              {ugcItems.length === 0 ? <p className="text-white/30 text-xs font-body">No pending submissions.</p> : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {ugcItems.map(u => (
                    <div key={u.id} className="bg-white/5 overflow-hidden" data-testid={`ugc-item-${u.id}`}>
                      <div className="aspect-square overflow-hidden"><img src={u.url} alt={u.guest_name} className="w-full h-full object-cover" loading="lazy" /></div>
                      <div className="p-3">
                        <p className="text-white text-xs font-body font-medium truncate">{u.guest_name}</p>
                        <p className="text-white/30 text-[10px] font-body">{u.event_title}</p>
                        <div className="flex gap-2 mt-2">
                          <button onClick={async () => { await api.patch(`/admin/ugc/${u.id}/moderate`, { status: "approved" }); setUgcItems(prev => prev.filter(i => i.id !== u.id)); toast.success("Approved"); }} className="flex-1 py-1 bg-green-500/15 text-green-400 text-[10px] font-body hover:bg-green-500/25 transition-colors" data-testid={`approve-${u.id}`}>Approve</button>
                          <button onClick={async () => { await api.patch(`/admin/ugc/${u.id}/moderate`, { status: "rejected" }); setUgcItems(prev => prev.filter(i => i.id !== u.id)); toast.success("Rejected"); }} className="flex-1 py-1 bg-red-500/15 text-red-400 text-[10px] font-body hover:bg-red-500/25 transition-colors" data-testid={`reject-${u.id}`}>Reject</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}

        {/* Contracts Tab */}
        {activeTab === "contracts" && (
          <motion.div initial="hidden" animate="visible" data-testid="contracts-tab">
            <motion.h3 variants={fadeUp} custom={0} className="text-white font-heading text-xl mb-6">Digital Contracts</motion.h3>
            <motion.div variants={fadeUp} custom={1} className="glass-card p-6">
              {contracts.length === 0 ? (
                <p className="text-white/30 text-xs font-body">No contracts yet. Contracts are created when you send them to clients from a booking.</p>
              ) : (
                <div className="space-y-3">
                  {contracts.map(c => (
                    <div key={c.id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0" data-testid={`contract-${c.id}`}>
                      <div>
                        <p className="text-white text-xs font-body font-medium">{c.template_name}</p>
                        <p className="text-white/30 text-[10px] font-body">{c.client_email} — Booking: {c.booking_id?.slice(0, 8)}...</p>
                      </div>
                      <span className={`text-[10px] font-body px-3 py-1 ${c.status === "signed" ? "text-green-400 bg-green-400/10" : "text-gold bg-gold/10"}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
        {/* Instagram Tab */}
        {activeTab === "instagram" && (
          <InstagramManagerInline />
        )}

        {/* Referrals Tab */}
        {activeTab === "referrals" && (
          <AdminReferralsTab />
        )}

        {/* Chauffeur Tab */}
        {activeTab === "chauffeur" && (
          <AdminChauffeurTab />
        )}

        {/* Manual Payments Tab */}
        {activeTab === "manual-payments" && (
          <AdminManualPaymentsTab />
        )}

        {/* Payouts Tab */}
        {activeTab === "payouts" && (
          <AdminPayoutsTab />
        )}

        {/* Heatmap Tab */}
        {activeTab === "heatmap" && (
          <AdminHeatmapTab />
        )}

        {/* Support Tab */}
        {activeTab === "support" && (
          <AdminSupportTab adminOnly={true} />
        )}

        {/* QR Codes Tab */}
        {activeTab === "qr-codes" && (
          <AdminQrTab />
        )}

        {/* Watermark Tab */}
        {activeTab === "watermark" && (
          <AdminWatermarkTab />
        )}

        {/* AI Studio Tab */}
        {activeTab === "studio" && (
          <AdminStudioTab />
        )}

        {/* Client Errors Tab */}
        {activeTab === "client-errors" && (
          <AdminClientErrorsTab />
        )}

        {/* Behavior AI Tab */}
        {activeTab === "behavior" && (
          <motion.div initial="hidden" animate="visible" data-testid="behavior-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <motion.p variants={fadeUp} custom={0} className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Marketing Intelligence</motion.p>
                <motion.h3 variants={fadeUp} custom={1} className="text-white font-heading text-xl">User Behavior & AI Recommendations</motion.h3>
              </div>
              <button onClick={async () => { setLoadingRecs(true); try { const res = await api.get("/admin/ai-recommendations"); setAiRecs(res.data); toast.success("AI recommendations generated!"); } catch { toast.error("Failed to generate."); } finally { setLoadingRecs(false); }}} disabled={loadingRecs} className="flex items-center gap-2 bg-gold/15 text-gold px-4 py-2 text-xs font-body hover:bg-gold/25 transition-colors disabled:opacity-40" data-testid="generate-recs-btn">
                <Sparkles size={14} /> {loadingRecs ? "Analyzing..." : "Generate AI Recommendations"}
              </button>
            </div>

            {behaviorData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Conversion Funnel */}
                <motion.div variants={fadeUp} custom={0} className="glass-card p-5 col-span-1 md:col-span-2 lg:col-span-1">
                  <p className="text-white/40 text-[10px] font-body uppercase tracking-wider mb-4">Conversion Funnel (7 days)</p>
                  <div className="space-y-3">
                    {[
                      { label: "Site Visits", val: behaviorData.funnel.total_visits, color: "bg-white/20" },
                      { label: "Booking Page", val: behaviorData.funnel.booking_page, color: "bg-gold/30" },
                      { label: "Bookings Made", val: behaviorData.funnel.bookings_made, color: "bg-green-400/30" },
                      { label: "Shop Visits", val: behaviorData.funnel.shop_visits, color: "bg-purple-400/30" },
                      { label: "CartBlancJA Views", val: behaviorData.funnel.gig_views, color: "bg-blue-400/30" },
                    ].map(f => (
                      <div key={f.label} className="flex items-center justify-between">
                        <span className="text-white/50 text-xs font-body">{f.label}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className={`h-full ${f.color} rounded-full`} style={{ width: `${Math.min((f.val / Math.max(behaviorData.funnel.total_visits, 1)) * 100, 100)}%` }} />
                          </div>
                          <span className="text-white text-xs font-body font-medium w-8 text-right">{f.val}</span>
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-white/5">
                      <span className="text-gold text-xs font-body">Conversion: {behaviorData.funnel.conversion_rate}%</span>
                    </div>
                  </div>
                </motion.div>

                {/* Top Pages */}
                <motion.div variants={fadeUp} custom={1} className="glass-card p-5">
                  <p className="text-white/40 text-[10px] font-body uppercase tracking-wider mb-4">Top Pages</p>
                  <div className="space-y-2">
                    {(behaviorData.top_pages || []).slice(0, 8).map((p, i) => (
                      <div key={p.page} className="flex items-center justify-between">
                        <span className="text-white/50 text-xs font-body truncate mr-2">{p.page}</span>
                        <span className="text-white text-xs font-body font-medium shrink-0">{p.views}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* User Interests */}
                <motion.div variants={fadeUp} custom={2} className="glass-card p-5">
                  <p className="text-white/40 text-[10px] font-body uppercase tracking-wider mb-4">User Interests (from behavior)</p>
                  {(behaviorData.chat_topics || []).length > 0 ? (
                    <div className="space-y-2">
                      {behaviorData.chat_topics.map(t => (
                        <div key={t.topic} className="flex items-center justify-between">
                          <span className="text-white/50 text-xs font-body">{(t.topic || "").replace(/_/g, " ")}</span>
                          <span className="text-gold text-xs font-body font-medium">{t.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/20 text-xs font-body">Data will populate as users interact with ARIA and the site.</p>
                  )}
                  {(behaviorData.service_interest || []).length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5">
                      <p className="text-white/30 text-[9px] font-body uppercase mb-2">Service Clicks</p>
                      {behaviorData.service_interest.map(s => (
                        <div key={s.service} className="flex items-center justify-between">
                          <span className="text-white/40 text-xs font-body">{s.service}</span>
                          <span className="text-white text-xs font-body">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              </div>
            ) : (
              <div className="text-center py-16 glass-card"><p className="text-white/30 font-body text-sm animate-pulse">Loading behavior data...</p></div>
            )}

            {/* AI Marketing Recommendations */}
            {aiRecs && (
              <motion.div variants={fadeUp} custom={3} className="glass-card p-6 mt-6" data-testid="ai-recs-panel">
                <p className="text-gold/60 text-[10px] font-body uppercase tracking-wider mb-1">AI Marketing Strategist</p>
                <h4 className="text-white font-body text-sm font-medium mb-4">Recommendations Based on User Behavior</h4>
                <div className="text-white/60 font-body text-xs leading-relaxed whitespace-pre-wrap">{aiRecs.recommendations}</div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Suggestions Tab — public feedback inbox with ARIA-powered
            digest. Auto-loads on first open. */}
        {activeTab === "suggestions" && (
          <motion.div initial="hidden" animate="visible" data-testid="suggestions-tab" onAnimationStart={() => {
            // Lazy-load data the first time the tab opens.
            if (!suggestionsDigest) loadSuggestionsDigest();
            if (suggestionsTabData.items.length === 0) loadSuggestionsTab();
          }}>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <motion.p variants={fadeUp} custom={0} className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Public Inbox</motion.p>
                <motion.h3 variants={fadeUp} custom={1} className="text-white font-heading text-xl">Suggestion Box</motion.h3>
                <motion.p variants={fadeUp} custom={2} className="text-white/40 text-xs font-body mt-1">
                  Floating "Suggest" button is live on every public page. ARIA reads each note + the team is paged on submit.
                </motion.p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => loadSuggestionsDigest(true)} disabled={loadingDigest} data-testid="refresh-digest-btn" className="text-xs font-body uppercase tracking-wider text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors disabled:opacity-50">
                  Refresh digest
                </button>
                <button onClick={runDeepDive} disabled={loadingDigest} data-testid="deep-dive-btn" className="text-xs font-body uppercase tracking-wider bg-gold text-luxury-black hover:bg-gold/90 px-3 py-2 font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50">
                  <Sparkles size={12} /> Analyse 30d
                </button>
              </div>
            </div>

            {/* ARIA digest panel */}
            <motion.div variants={fadeUp} custom={3} className="bg-luxury-charcoal border border-gold/20 p-6 mb-6" data-testid="aria-digest-panel">
              <div className="flex items-center gap-2 text-gold text-[10px] font-body uppercase tracking-[0.25em] mb-3">
                <Sparkles size={13} /> ARIA {suggestionsDigest?._deep ? "Deep Dive (30 days)" : "Weekly Digest"}
              </div>
              {loadingDigest && !suggestionsDigest ? (
                <p className="text-white/40 font-body text-xs animate-pulse">ARIA is reviewing recent suggestions...</p>
              ) : suggestionsDigest ? (
                <>
                  <pre className="text-white/80 font-body text-sm leading-relaxed whitespace-pre-wrap">{suggestionsDigest.summary}</pre>
                  <p className="text-white/30 text-[10px] font-body mt-3">
                    Based on {suggestionsDigest.item_count} suggestion(s) · {suggestionsDigest.cached ? "cached" : "fresh"} · {suggestionsDigest.generated_at ? new Date(suggestionsDigest.generated_at).toLocaleString() : "—"}
                  </p>
                </>
              ) : (
                <p className="text-white/40 font-body text-xs">Click Refresh digest to generate ARIA's analysis.</p>
              )}
            </motion.div>

            {/* Status pill row */}
            <motion.div variants={fadeUp} custom={4} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
              {["new", "in_review", "actioned", "archived"].map((k) => (
                <div key={k} className="bg-luxury-charcoal border border-white/5 p-3" data-testid={`suggestion-count-${k}`}>
                  <p className="text-[10px] font-body uppercase tracking-wider text-white/40">{k.replace("_", " ")}</p>
                  <p className="text-white font-heading text-2xl">{suggestionsTabData.counts[k] || 0}</p>
                </div>
              ))}
            </motion.div>

            {/* Submissions list */}
            <motion.h3 variants={fadeUp} custom={5} className="text-white font-heading text-lg mb-4">
              Recent submissions ({suggestionsTabData.items.length})
            </motion.h3>
            {suggestionsTabData.items.length === 0 ? (
              <div className="text-center py-16 border border-white/5">
                <Lightbulb size={36} className="text-white/15 mx-auto mb-4" />
                <p className="text-white/40 font-body text-sm">No suggestions yet.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="suggestions-list">
                {suggestionsTabData.items.map((s) => (
                  <div key={s.id} className="bg-luxury-charcoal border border-white/5 p-4" data-testid={`suggestion-row-${s.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-gold/70 text-[10px] font-body uppercase tracking-wider">{s.category || "general"}</span>
                          <span className={`text-[10px] font-body uppercase tracking-wider px-1.5 py-0.5 ${s.status === "new" ? "bg-gold/20 text-gold" : s.status === "actioned" ? "bg-green-500/20 text-green-400" : "text-white/40 bg-white/5"}`}>
                            {s.status || "new"}
                          </span>
                          <span className="text-white/30 text-[10px] font-body">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</span>
                          {s.page_url && (
                            <a href={s.page_url} target="_blank" rel="noreferrer" className="text-white/30 text-[10px] font-body underline hover:text-white/60 truncate max-w-[200px]">
                              {s.page_url.replace(/^https?:\/\//, "").slice(0, 40)}
                            </a>
                          )}
                        </div>
                        <p className="text-white text-sm font-body leading-relaxed whitespace-pre-wrap">{s.message || s.content || ""}</p>
                        <p className="text-white/40 text-[11px] font-body mt-2">{s.name || "Anonymous"}{s.email ? ` · ${s.email}` : ""}</p>
                      </div>
                    </div>
                    {(s.status || "new") === "new" && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 flex-wrap">
                        <button onClick={() => setSuggestionStatus(s.id, "in_review")} data-testid={`sg-review-${s.id}`} className="text-xs font-body text-gold hover:text-gold/80 px-2 py-1">Mark in review</button>
                        <button onClick={() => setSuggestionStatus(s.id, "actioned")} data-testid={`sg-actioned-${s.id}`} className="text-xs font-body text-green-400 hover:text-green-300 px-2 py-1">Mark actioned</button>
                        <button onClick={() => setSuggestionStatus(s.id, "archived")} data-testid={`sg-archive-${s.id}`} className="text-xs font-body text-white/50 hover:text-white px-2 py-1">Archive</button>
                        <button onClick={() => convertSuggestionToJob(s.id, s.message || s.content)} data-testid={`sg-convert-${s.id}`} className="text-xs font-body text-purple-300 hover:text-purple-200 px-2 py-1 flex items-center gap-1 ml-auto">
                          <Sparkles size={10} /> Convert → Job
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Settings Tab — platform-wide feature flags (manual control over
            AI retouch and auto-watermark for gallery uploads) */}
        {activeTab === "settings" && (
          <motion.div initial="hidden" animate="visible" data-testid="settings-tab">
            <div className="mb-6">
              <motion.p variants={fadeUp} custom={0} className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Platform Controls</motion.p>
              <motion.h3 variants={fadeUp} custom={1} className="text-white font-heading text-xl">Studio &amp; Gallery Automation</motion.h3>
              <motion.p variants={fadeUp} custom={2} className="text-white/40 text-xs font-body mt-1">
                Toggle the automated AI pipeline steps. Useful when your in-house editor has already retouched or watermarked the photos manually.
              </motion.p>
            </div>

            {platformSettings === null ? (
              <div className="text-center py-16 glass-card"><p className="text-white/30 font-body text-sm animate-pulse">Loading platform settings...</p></div>
            ) : (
              <div className="space-y-3 max-w-2xl" data-testid="platform-settings-list">
                {[
                  {
                    key: "studio_ai_retouch_enabled",
                    label: "AI Intelligent Retouch (Nano Banana)",
                    description: "When ON, the Studio pipeline runs Gemini Nano Banana on uploaded photos to smooth skin, fix red-eye, and correct subtle flaws. When OFF, photos still get the deterministic OpenCV grade (white balance, HDR, denoise) but no AI flaw correction.",
                    icon: Sparkles,
                  },
                  {
                    key: "gallery_auto_watermark_enabled",
                    label: "Auto-Watermark on Gallery Upload",
                    description: "When ON, every photo uploaded into a gallery gets the Aristocrats JA Signature logo overlaid automatically. Turn OFF when your editor has already burned a custom watermark into the image so the platform doesn't double-stamp.",
                    icon: Shield,
                  },
                ].map((s, i) => {
                  const Icon = s.icon;
                  const value = !!platformSettings[s.key];
                  return (
                    <motion.div
                      key={s.key}
                      variants={fadeUp}
                      custom={i + 3}
                      className="flex items-start gap-4 p-5 bg-luxury-charcoal border border-white/5 hover:border-white/10 transition-colors"
                      data-testid={`setting-row-${s.key}`}
                    >
                      <div className={`shrink-0 w-9 h-9 flex items-center justify-center rounded ${value ? "bg-gold/15 text-gold" : "bg-white/5 text-white/30"}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <p className="text-white font-body text-sm font-medium">{s.label}</p>
                          <span
                            className={`text-[10px] font-body uppercase tracking-wider ${value ? "text-gold" : "text-white/30"}`}
                            data-testid={`setting-status-${s.key}`}
                          >
                            {value ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-white/40 text-xs font-body leading-relaxed">{s.description}</p>
                      </div>
                      <button
                        onClick={() => togglePlatformSetting(s.key, !value)}
                        disabled={savingSettings}
                        role="switch"
                        aria-checked={value}
                        data-testid={`toggle-${s.key}`}
                        className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${value ? "bg-gold" : "bg-white/15"}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-luxury-black transition-transform ${value ? "translate-x-6" : "translate-x-1"}`}
                        />
                      </button>
                    </motion.div>
                  );
                })}
                <p className="text-white/30 text-[11px] font-body mt-4">
                  Changes apply to future uploads only. Already-uploaded photos keep their current state — use Smart Reprocess in the Gallery Manager to re-run the pipeline on existing images.
                </p>
              </div>
            )}
          </motion.div>
        )}

      </div>
      {lightboxImg && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-8" onClick={() => setLightboxImg(null)} data-testid="lightbox-modal">
          <button onClick={() => setLightboxImg(null)} className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10" data-testid="lightbox-close"><X size={24} /></button>
          <img src={lightboxImg} alt="Full view" className="max-w-full max-h-full object-contain rounded" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
